/** A subform on the card: its display name and the workflow template(s) it draws from. */
export interface CardSummarySubform {
  name: string;
  workflowTemplateIds: string[];
}

/**
 * Builds the prompt sent to the Ask gateway for a whole-card summary. The card scope
 * already gives the agent the entire card, so the prompt does not embed field values.
 * It does, however, name the card's own workflow template and each subform's workflow
 * template so the agent can tell the parent card apart from the subforms nested on it,
 * and it explicitly instructs the agent to open and consider the subform field data —
 * otherwise concerning subform values (a failed check, a poor condition) can be missed
 * at the card level even though the subform's own summary surfaces them.
 */
export function buildCardSummaryPrompt({
  workflowTemplateId,
  subforms,
}: {
  workflowTemplateId: string;
  subforms: CardSummarySubform[];
}): string {
  const subformBlock =
    subforms.length > 0
      ? subforms
          .map(
            subform =>
              `- "${
                subform.name
              }" — subform workflow template id(s): ${subform.workflowTemplateIds.join(', ')}`,
          )
          .join('\n')
      : '(This card has no subforms.)';

  return [
    'You are summarizing an entire card in Coast. Consider EVERYTHING about this card: ' +
      'all of its fields and their current values, the contents of any subforms on it, and — ' +
      'if available to you — the related or linked cards attached to it.',
    '',
    `This card itself is based on the workflow template with id ${workflowTemplateId}. The ` +
      'subforms nested on this card are based on their OWN, separate workflow templates:',
    subformBlock,
    '',
    'Open and inspect the field data inside EACH subform above and factor it into this ' +
      'summary. Subform fields often carry the most important signals — for example a check ' +
      'whose result is FAIL, a condition rated POOR, or an overdue item. Treat a concerning ' +
      'value inside a subform as just as important as one on the card itself, and surface it ' +
      'here at the card level; do not assume the subform is fine just because its top-level ' +
      'fields look complete.',
    '',
    'If recent change history for this card is available to you, factor it in and call out ' +
      'notable recent changes; otherwise summarize the current state only.',
    '',
    'Write for a busy operator who is skimming. Include ONLY what is actionable or needs ' +
      'attention — anything failing, overdue, blocking, out of spec, unanswered, or otherwise ' +
      'concerning. For each such item, put the specific thing that needs attention in ' +
      '**bold**. Do NOT pad the summary with routine, healthy, or reassuring status just to ' +
      'fill space.',
    '',
    'If nothing needs attention, do not invent concerns — respond with a single bullet ' +
      'stating that everything looks on track and nothing needs attention right now.',
    '',
    'Start each bullet with a relevant emoji and separate the bullets with a blank line. ' +
      'Respond with NO MORE THAN 3 bullet points and nothing else — no preamble, no heading, ' +
      'no closing remark.',
  ].join('\n');
}
